<script lang="ts">
	import { appName } from "../../../core/schema/names/appName.js";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import Layout from "../components/Layout.svelte";
	import StaticPicture from "../components/Picture/StaticPicture.svelte";
	import type { ProductProps } from "./ProductProps.js";
	import logo from "../../../core/assets/logo.svg";
	import ProductMobileGallery from "../components/ProductMobileGallery.svelte";
	import DynamicPicture from "../components/Picture/DynamicPicture.svelte";
	import ProductDesktopGallery from "../components/ProductDesktopGallery.svelte";
	import Content from "../components/Content.svelte";
	import ContactForm from "../components/Contact/ContactForm.svelte";
	import ProductInquiryModal from "../components/ProductInquiryModal.svelte";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./Product.translations.json";
	import type { ProductTechnicalParameter } from "../../../core/schema/Product.js";
	import Button from "../components/Button.svelte";
	import Link from "../components/Link.svelte";
	import Icon from "../components/Icon.svelte";
	import arrowRight from "../../../core/assets/icons/arrow-right.svg?raw";
	import Breadcrumbs from "../components/Breadcrumbs.svelte";
	import { getProductDetailPath } from "../../../core/schema/paths/getProductDetailPath.js";
	import { getReferenceDetailPath } from "../../../core/schema/paths/getReferenceDetailPath.js";
	import { contactPathMap } from "../../../core/schema/paths/contactPathMap.js";
	import { categoryNameMap } from "../../../core/schema/Category.js";
	import { getContext, setContext } from "svelte";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import { getTenant } from "../getTenant.js";
	import type { GlobalTemplateProps } from "../GlobalTemplateProps.js";
	import { pageSlugContextKey } from "../contexts/pageSlugContextKey.js";

	type $$Props = ProductProps;
	const { product, references, relatedProducts, breadcrumbs } = $$restProps as $$Props;
	const { STORAGE_URL } = getGlobalSettings();
	const tenant = getTenant();
	const pageSlug = getContext<GlobalTemplateProps["pageSlug"]>(pageSlugContextKey);

	$: mainParameters = product.technicalParameters
		.filter((p) => typeof p.mainOrder === "number")
		.sort((a, b) => (a.mainOrder ?? 0) - (b.mainOrder ?? 0));

	$: groupedParameters = product.technicalParameters.reduce<Record<string, ProductTechnicalParameter[]>>(
		(acc, parameter) => {
			(acc[parameter.category] ??= []).push(parameter);
			return acc;
		},
		{},
	);

	const translate = getTranslate(translations);
	setContext(thumbnailImageContextKey, `${STORAGE_URL}/${product.primaryImage.path}`);

	$: caseStudies = references.slice(0, 2);
	$: typicalDeployments = product.typicalDeployments ?? [];
	$: keyFeaturesHeadline = translate("keyFeaturesHeadline", { name: product.name });
	$: typicalHeadline = translate("typicalDeploymentsHeadline", { name: product.name });
	$: customCtaHeadline = translate("customCtaHeadline", { name: product.name });

	let showInquiryModal = false;
</script>

<Layout>
	<Breadcrumbs pageSlug={pageSlug[tenant.locale] ?? ""} {breadcrumbs} showBackLink={false} />
	<section class="container">
		<div class="mx-auto mt-4 w-full max-w-[120rem] px-4 md:px-16 xl:px-32">
			<div class="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-16">
				<!-- Left: image gallery (desktop + mobile) -->
				<div class="max-w-full">
					<ProductDesktopGallery {product} media={product.images} class="hidden lg:block" />
					{#if product.images.length === 0}
						<div class="lg:hidden">
							<div class="bg-lttertiary-100 flex aspect-[4/3] h-auto w-full items-center justify-center">
								<StaticPicture
									alt="Logo {appName}"
									image={logo}
									class="w-28 opacity-50 sm:w-36"
									loading="eager"
									width={768}
									height={576}
								/>
							</div>
						</div>
					{:else if product.images.length > 1}
						<ProductMobileGallery {product} media={product.images} class="lg:hidden" />
					{:else}
						{@const [image] = product.images}
						{#if image}
							<div class="lg:hidden">
								<DynamicPicture
									imgClass="aspect-[4/3] object-cover"
									{image}
									width={768}
									height={576}
									alt="{translate('photo')} – {product.name}"
									lightboxId={product.id}
									lightboxWidth={1000}
									bucket="storage"
									loading="eager"
									fetchpriority="high"
									preload
									sourcesets={{
										"<md": [490, 368],
										">md": [650, 488],
										">lg": [456, 342],
										">xl": [584, 438],
									}}
								/>
							</div>
						{/if}
					{/if}
				</div>

				<!-- Right: product info -->
				<div class="flex flex-col">
					{#if product.tags.length > 0}
						<div class="mb-5 flex flex-wrap gap-2">
							{#each product.tags as tag}
								<span
									class="bg-ltprimary-200 text-ltprimary font-lexend text-2xs/[1.1] inline-flex items-center px-3 py-2 font-medium uppercase tracking-[0.08em]"
								>
									{nbspify(tag)}
								</span>
							{/each}
						</div>
					{/if}
					<h1
						class="text-ltprimary text-2lg/tight m-0 p-0 font-light uppercase tracking-[0.02em] md:text-2xl xl:text-3xl"
					>
						{nbspify(product.name)}
					</h1>

					{#if product.subheadline}
						<p
							class="text-ltprimary-300 font-lexend !mb-0 mt-4 !text-[1.125rem] font-light leading-[1.8rem] tracking-[0.01em]"
						>
							{nbspify(product.subheadline)}
						</p>
					{/if}

					{#if product.content}
						<div
							class="text-ltsecondary [&_ul>li]:text-ltprimary mt-8 text-base/[1.6]
								tracking-normal [&_p]:!text-[1.0625rem]/[1.7rem]
								[&_p]:tracking-normal [&_ul>li]:relative [&_ul>li]:pl-9 [&_ul>li]:text-[0.9375rem]/6 [&_ul>li]:tracking-normal
								[&_ul>li]:[list-style:none] [&_ul>li]:before:absolute [&_ul>li]:before:left-0
								[&_ul>li]:before:top-0.5 [&_ul>li]:before:size-[1.375rem] [&_ul>li]:before:bg-[url('data:image/svg+xml;utf8,%3Csvg%20xmlns=%22http://www.w3.org/2000/svg%22%20width=%2222%22%20height=%2222%22%20fill=%22none%22%20viewBox=%220%200%2022%2022%22%3E%3Crect%20width=%2222%22%20height=%2222%22%20rx=%2211%22%20fill=%22%23E4E0FF%22/%3E%3Cpath%20stroke=%22%235B47FF%22%20stroke-linecap=%22round%22%20stroke-linejoin=%22round%22%20stroke-width=%221.833%22%20d=%22M7.332%2011l2.75%202.75%204.583-5.5%22/%3E%3C/svg%3E')] [&_ul>li]:before:bg-contain
								[&_ul>li]:before:bg-no-repeat [&_ul>li]:before:content-[''] [&_ul]:m-0
								[&_ul]:flex [&_ul]:list-none [&_ul]:flex-col
								[&_ul]:gap-3
								[&_ul]:p-0"
						>
							<Content content={product.content} />
						</div>
					{/if}

					<div class="mt-10 flex flex-wrap items-center gap-4">
						<Button
							icon={arrowRight}
							variant="primary"
							on:click={() => {
								showInquiryModal = true;
							}}
						>
							{translate("requestProduct")}
						</Button>
						<Link asButton variant="secondary" icon={arrowRight} href="#technicke-parametry">
							{translate("technicalParameters")}
						</Link>
					</div>

					{#if mainParameters.length > 0}
						<div class="border-lttertiary mt-12 grid grid-cols-1 border sm:grid-cols-3 lg:mt-16">
							{#each mainParameters as prop, index}
								<div
									class="border-lttertiary flex min-w-0 flex-col justify-center gap-1 break-words px-4 py-4 sm:gap-1.5 sm:px-3 sm:py-5 md:px-[1.375rem] {index >
									0
										? 'border-t sm:border-l sm:border-t-0'
										: ''}"
								>
									<span
										class="text-ltmeta font-lexend text-[0.6875rem]/[1.1rem] font-normal uppercase tracking-[0.06em]"
									>
										{nbspify(prop.name)}
									</span>
									<span
										class="text-ltprimary font-saira text-xl/[1.1] font-normal tracking-normal md:text-[1.625rem]/[1.625rem]"
									>
										{nbspify(prop.value)}
									</span>
								</div>
							{/each}
						</div>
					{/if}
				</div>
			</div>
			{#if product.keyFeatures.length > 0}
				<section class="mt-16 lg:mt-24">
					<div class="mx-auto max-w-[120rem]">
						<div class="mb-6 flex items-center gap-4">
							<span class="bg-ltprimary-200 h-px w-8"></span>
							<span class="text-ltprimary-200 font-lexend text-xs/[1.6] font-light uppercase tracking-[0.16em]">
								{translate("keyFeaturesTag")}
							</span>
						</div>
						<h2 class="text-ltprimary mb-8 p-0 font-light uppercase">
							{keyFeaturesHeadline}
						</h2>
					</div>

					<div class="border-lttertiary border-y">
						<div class="mx-auto max-w-[120rem]">
							<ol
								class="border-lttertiary -mb-px grid list-none grid-cols-1 border-l [counter-reset:feature] md:grid-cols-2 lg:grid-cols-3"
							>
								{#each product.keyFeatures as feature}
									<li
										class="border-lttertiary bg-lttertiary-100 before:border-ltprimary-300 before:text-ltprimary-300 before:font-lexend flex flex-col gap-16 border-b border-r p-8 before:flex before:size-10 before:shrink-0 before:items-center before:justify-center before:rounded-full before:border before:text-center before:text-base/[1.6] before:font-normal before:tabular-nums before:tracking-normal before:content-[counter(feature,decimal-leading-zero)] before:[counter-increment:feature]"
									>
										<div class="flex flex-col gap-6">
											<h3 class="text-ltprimary text-2md/none m-0 font-normal uppercase tracking-[0.02em]">
												{nbspify(feature.title)}
											</h3>
											<p class="text-ltsecondary font-lexend !m-0 !text-base/[1.6] font-normal tracking-normal">
												{nbspify(feature.description)}
											</p>
										</div>
									</li>
								{/each}
							</ol>
						</div>
					</div>
				</section>
			{/if}

			{#if product.technicalParameters.length > 0}
				<section id="technicke-parametry" class="mt-16 lg:mt-24">
					<div class="mb-4 flex items-center gap-4">
						<span class="bg-ltprimary-200 h-px w-8"></span>
						<span class="text-ltprimary-200 font-lexend text-xs/[1.6] font-light uppercase tracking-[0.16em]">
							{translate("technicalParameters")}
						</span>
					</div>

					<h2
						class="text-ltprimary text-2lg/tight m-0 mb-12 p-0 font-light uppercase tracking-[0.02em] md:text-2xl lg:mb-16 xl:text-3xl"
					>
						{translate("specifications")}
					</h2>

					<div class="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-12 lg:gap-16">
						{#each Object.entries(groupedParameters) as [categoryName, parameters]}
							<table class="border-lttertiary w-full border-separate border-spacing-0 self-start border-b text-left">
								<caption
									class="bg-lttertiary-300 border-lttertiary text-ltprimary font-saira text-md/8 border p-6 text-left font-normal uppercase tracking-[0.02em]"
								>
									{nbspify(categoryName)}
								</caption>
								<tbody>
									{#each parameters as parameter}
										<tr>
											<th
												scope="row"
												class="text-ltprimary font-lexend border-lttertiary w-1/2 border-b py-[1.125rem] pl-6 pr-2 align-middle text-[0.9375rem]/none font-medium tracking-normal"
											>
												{nbspify(parameter.name)}
											</th>
											<td
												class="text-ltsecondary font-lexend border-lttertiary border-b py-[1.125rem] pl-2 pr-6 align-middle text-[0.9375rem]/none font-normal tracking-normal"
											>
												{nbspify(parameter.value)}
											</td>
										</tr>
									{/each}
								</tbody>
							</table>
						{/each}
					</div>

					<p class="text-ltmeta font-lexend !mb-0 mt-8 !text-[0.8125rem]/[1.3rem] font-normal tracking-normal lg:mt-12">
						{translate("technicalParametersNote")}
					</p>
				</section>
			{/if}
		</div>
	</section>

	<!-- TYPICKÉ NASAZENÍ / Kde dává {product} smysl -->
	{#if typicalDeployments.length > 0}
		<section class="container mt-16 md:mt-24">
			<div class="mx-auto w-full max-w-[120rem] px-4 md:px-16 xl:px-32">
				<div class="mb-6 flex items-center gap-4">
					<span class="bg-ltprimary-200 h-px w-8"></span>
					<span class="text-ltprimary-200 font-lexend text-xs/[1.6] font-light uppercase tracking-[0.16em]">
						{translate("typicalDeploymentsTag")}
					</span>
				</div>
				<h2 class="text-ltprimary mb-8 p-0 font-light uppercase tracking-wider md:mb-12">
					{typicalHeadline}
				</h2>
				<ol
					class="border-lttertiary grid list-none grid-cols-1 border-l border-t [counter-reset:deployment] md:grid-cols-2"
				>
					{#each typicalDeployments as deployment}
						<li
							class="border-lttertiary before:font-saira before:text-lttertiary flex gap-4 border-b border-r p-6 before:shrink-0 before:text-[1.875rem]/none before:font-light before:tabular-nums before:tracking-normal before:content-[counter(deployment,decimal-leading-zero)] before:[counter-increment:deployment] md:gap-8 md:p-8"
						>
							<div class="flex min-w-0 flex-col gap-2 [overflow-wrap:anywhere]">
								<h3 class="text-ltprimary font-lexend m-0 text-base/[1.6] font-medium tracking-normal">
									{nbspify(deployment.name)}
								</h3>
								<p class="text-ltsecondary font-lexend !m-0 !text-xs/[1.6] font-normal tracking-normal">
									{nbspify(deployment.value)}
								</p>
							</div>
						</li>
					{/each}
				</ol>
			</div>
		</section>
	{/if}

	<!-- NAVRHNEME {product} NA MÍRU VAŠÍ HALE -->
	<section class="bg-ltprimary border-ltprimary-100 mt-12 border text-white md:mt-16">
		<div
			class="mx-auto flex w-full max-w-[120rem] flex-col gap-8 px-4 py-8 md:px-16 md:py-14 lg:flex-row lg:items-center lg:justify-between lg:gap-16 xl:px-32"
		>
			<div class="flex-1">
				<h2 class="font-saira mb-4 p-0 text-[2.375rem]/[1.1] font-light uppercase tracking-[0.02em] text-white">
					{nbspify(customCtaHeadline)}
				</h2>
				<p class="font-lexend m-0 max-w-2xl text-base/[1.6] font-light tracking-normal text-white/80">
					{translate("customCtaDescription")}
				</p>
			</div>
			<a
				class="font-roboto group relative flex h-12 min-h-12 w-full shrink-0 items-center justify-between rounded-3xl border border-white pl-6 pr-1 text-base font-medium uppercase tracking-wider text-white no-underline lg:w-[20.418rem]"
				href="/{contactPathMap[tenant.locale]}"
			>
				<span
					class="group-hover:text-ltprimary-300 group-active:text-ltprimary-300 relative z-10 transition-colors duration-300"
				>
					{translate("customCtaButton")}
				</span>
				<span
					class="absolute inset-y-1 left-[calc(100%-2.75rem)] right-1 z-0 rounded-full bg-white transition-[left] duration-300 ease-in-out group-hover:left-1 group-active:left-1"
				></span>
				<span class="text-ltprimary-300 relative z-10 flex size-10 shrink-0 items-center justify-center">
					<Icon icon={arrowRight} />
				</span>
			</a>
		</div>
	</section>

	<!-- MOHLO BY SE HODIT / Související produkty -->
	{#if relatedProducts.length > 0}
		<section class="container mt-16 md:mt-24">
			<div class="mx-auto w-full max-w-[120rem] px-4 md:px-16 xl:px-32">
				<div class="mb-6 flex items-center gap-4">
					<span class="bg-ltprimary-200 h-px w-8"></span>
					<span class="text-ltprimary-200 font-lexend text-xs/[1.6] font-light uppercase tracking-[0.16em]">
						{translate("relatedProductsTag")}
					</span>
				</div>
				<h2 class="text-ltprimary mb-8 p-0 font-light uppercase tracking-wider md:mb-12">
					{translate("relatedProductsHeadline")}
				</h2>
				<div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
					{#each relatedProducts as related (related.id)}
						{@const detailPath = `/${getProductDetailPath(related.category, related)}`}
						<article class="border-lttertiary group flex flex-col border bg-white p-5">
							<a href={detailPath} class="relative block w-full" aria-label={related.name}>
								<div class="bg-lttertiary-100 relative aspect-[4/3] w-full overflow-hidden">
									{#if related.primaryImage}
										<DynamicPicture
											bucket="storage"
											image={related.primaryImage}
											alt={related.name}
											width={480}
											height={360}
											loading="lazy"
											imgClass="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-105"
											class="absolute inset-0 size-full"
										/>
									{:else}
										<div class="absolute inset-0 flex items-center justify-center">
											<StaticPicture
												image={logo}
												alt="Logo {appName}"
												width={120}
												height={120}
												class="w-20 opacity-40"
												loading="lazy"
											/>
										</div>
									{/if}
									{#if related.tags.length > 0}
										<div class="absolute left-3 top-3 flex flex-wrap gap-1.5">
											{#each related.tags as tag}
												<span
													class="bg-ltprimary-200 text-ltprimary font-lexend text-3xs/[1.1] inline-flex items-center px-2 py-1.5 font-medium uppercase tracking-[0.08em]"
												>
													{nbspify(tag)}
												</span>
											{/each}
										</div>
									{:else}
										<span
											class="bg-ltprimary-200 text-ltprimary font-lexend text-3xs/[1.1] absolute left-3 top-3 inline-flex items-center px-2 py-1.5 font-medium uppercase tracking-[0.08em]"
										>
											{categoryNameMap[related.category][tenant.locale]}
										</span>
									{/if}
								</div>
							</a>
							<div class="flex flex-1 flex-col gap-3 pt-6">
								<h3 class="text-ltprimary font-saira m-0 text-[1.375rem]/[1.1] font-normal uppercase tracking-[0.01em]">
									<a href={detailPath} class="hover:underline">
										{nbspify(related.name)}
									</a>
								</h3>
								<p class="text-ltsecondary text-2xs/[1.6] m-0 line-clamp-3 flex-1 font-normal">
									{nbspify(related.perex)}
								</p>
								<a
									href={detailPath}
									class="text-ltprimary-300 hover:text-ltprimary-100 font-saira group/link mt-2 inline-flex w-fit items-center gap-3 text-xs/[1.6] font-bold uppercase tracking-[0.06em] no-underline transition-colors duration-300"
								>
									<span class="relative">
										{translate("productDetail")}
										<span
											class="absolute inset-x-0 -bottom-0.5 h-px origin-left scale-x-0 bg-current transition-transform duration-300 ease-out group-hover/link:scale-x-100"
										></span>
									</span>
									<span
										class="border-lttertiary bg-lttertiary-100 group-hover/link:bg-lttertiary-300 flex size-5 shrink-0 items-center justify-center rounded-full border transition-colors duration-300"
									>
										<Icon icon={arrowRight} class="h-2.5 w-1.5" />
									</span>
								</a>
							</div>
						</article>
					{/each}
				</div>
			</div>
		</section>
	{/if}

	<!-- PŘÍPADOVÉ STUDIE / Reference -->
	{#if caseStudies.length > 0}
		<section class="container mt-16 md:mt-24">
			<div class="mx-auto w-full max-w-[120rem] px-4 md:px-16 xl:px-32">
				<div class="mb-6 flex items-center gap-4">
					<span class="bg-ltprimary-200 h-px w-8"></span>
					<span class="text-ltprimary-200 font-lexend text-xs/[1.6] font-light uppercase tracking-[0.16em]">
						{translate("caseStudiesTag")}
					</span>
				</div>
				<h2 class="text-ltprimary mb-8 p-0 font-light uppercase md:mb-12">
					{translate("caseStudiesHeadline")}
				</h2>
				<div class="grid grid-cols-1 gap-7 lg:grid-cols-2">
					{#each caseStudies as reference (reference.id)}
						{@const primaryImage = reference.previewImage ?? reference.images[0]}
						<article class="border-lttertiary flex flex-col gap-8 border bg-white p-8">
							<a
								href="/{getReferenceDetailPath(reference)}"
								class="group relative block aspect-[4/3] w-full overflow-hidden"
								aria-label={reference.title}
							>
								{#if primaryImage}
									<DynamicPicture
										bucket="storage"
										image={primaryImage}
										alt={reference.title}
										width={760}
										height={570}
										loading="lazy"
										imgClass="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-105"
										class="absolute inset-0 size-full"
									/>
								{:else}
									<div class="bg-lttertiary-100 absolute inset-0 flex items-center justify-center">
										<StaticPicture
											image={logo}
											alt="Logo {appName}"
											width={160}
											height={160}
											class="w-28 opacity-40"
											loading="lazy"
										/>
									</div>
								{/if}
								<span
									class="text-ltprimary-300 bg-lttertiary-100 font-saira text-2xs/[1.6] absolute left-px top-px px-2.5 pb-[0.386875rem] pt-[0.3125rem] font-medium uppercase tracking-[0.07em]"
								>
									{translate("caseStudyLabel")}
								</span>
							</a>
							<div class="flex flex-1 flex-col gap-4">
								<h3 class="text-ltprimary font-saira m-0 text-[1.375rem]/[1.1] font-normal uppercase tracking-[0.01em]">
									<a href="/{getReferenceDetailPath(reference)}" class="no-underline hover:underline">
										{nbspify(reference.title)}
									</a>
								</h3>
								<p class="text-ltsecondary font-lexend !m-0 line-clamp-3 !text-base/[1.6] font-normal tracking-normal">
									{nbspify(reference.perex)}
								</p>
							</div>
							<Link
								variant="tertiary"
								icon={arrowRight}
								iconGap="mr-0"
								iconClass="size-6 rounded-full border border-current p-1.5"
								href="/{getReferenceDetailPath(reference)}"
								class="font-saira w-fit !gap-2.5 !text-xs/[1.6] !font-bold uppercase tracking-[0.06em]"
							>
								{translate("readMore")}
							</Link>
						</article>
					{/each}
				</div>
			</div>
		</section>
	{/if}

	<!-- OZVĚTE SE NÁM (contact form) -->
	<section class="mt-16 md:mt-24">
		<ContactForm />
	</section>

	<ProductInquiryModal bind:show={showInquiryModal} productId={product.id} productName={product.name} />
</Layout>
